import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

// Neutral variants take their text colour from the control-foreground token
// (it flips with the mode), so a call-site `text-destructive` replaces it in
// both modes instead of losing to a dark: variant.
// Mirrors Unnati ERP's Button (unnati_frontend/src/components/ui/Button/Button.tsx
// + assets/styles/components/_button.css) exactly:
//   md: 48px tall (--control-h-btn), 12px radius (rounded-xl there = rounded-lg
//   here, Setu's lg token is 12px), px-5 py-2, text-sm (14px) bold Inter,
//   4px icon gap, 20px Tabler-geometry icon (stroke 2), no shadow,
//   active:scale-[0.98] press feedback, disabled = 50% opacity.
//   solid ("default"): #2A85FF bg, white text, hover #4996FF.
//   default ("outline"): white bg, 1px #D4D4D4 border, #525252 text; hover
//   border + 1px ring + text go primary.
// Heights come from the --control-h* custom properties (see index.css) so a
// dense surface can opt into compact controls with data-density="compact".
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg font-bold outline-none transition-transform active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // "solid"
        default: 'bg-primary text-primary-foreground hover:bg-primary-mild',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-error-600',
        // bordered white button that picks up the primary on hover
        outline: 'border border-clay-300 bg-white text-control-foreground hover:border-primary hover:text-primary hover:ring-1 hover:ring-primary dark:border-clay-700 dark:bg-clay-700',
        secondary: 'border border-clay-300 bg-white text-control-foreground hover:border-primary hover:text-primary hover:ring-1 hover:ring-primary dark:border-clay-700 dark:bg-clay-700',
        // "plain"
        ghost: 'text-control-foreground hover:bg-accent hover:text-foreground active:bg-accent/70',
        link: 'text-primary underline-offset-4 hover:text-primary-mild hover:underline active:scale-100',
        success: 'bg-success text-white hover:bg-success/90 active:bg-success-600',
        warning: 'bg-warning text-white hover:bg-warning/90 active:bg-warning-600',
        // kept for existing call sites — identical to the solid primary
        lime: 'bg-primary text-primary-foreground hover:bg-primary-mild',
      },
      size: {
        default: 'h-[var(--control-h-btn)] px-5 py-2 text-sm [&_svg]:size-5',
        sm: 'h-[var(--control-h-sm)] px-3 py-2 text-sm [&_svg]:size-[1.125rem]',
        xs: 'h-[var(--control-h-xs)] rounded-md px-3 text-sm [&_svg]:size-4',
        lg: 'h-[var(--control-h-lg)] px-8 py-2 text-base [&_svg]:size-5',
        icon: 'h-[var(--control-h-sm)] w-[var(--control-h-sm)] p-0 [&_svg]:size-[1.125rem]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
